import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export interface SortedItem {
  index: number;
  selectedOption: string;
}

interface MicroscopeSorterState {
  currentIndex: number;
  selectedOption: string | null;
  sortedItems: SortedItem[];
  totalCount: number;
  randomizedImages: string[];
  isComplete: boolean;
}

const initialState: MicroscopeSorterState = {
  currentIndex: 0,
  selectedOption: null,
  sortedItems: [],
  totalCount: 0,
  randomizedImages: [],
  isComplete: false,
};

interface SelectOptionPayload {
  option: string;
}

interface NavigatePayload {
  index: number;
}

interface SubmitPayload {
  index: number;
  option: string;
}

interface InitializePayload {
  totalCount: number;
  images: string[];
  randomize: boolean;
}

const microscopeSorterSlice = createSlice({
  name: "microscopeSorter",
  initialState,
  reducers: {
    initialize: (state, action: PayloadAction<InitializePayload>) => {
      state.totalCount = action.payload.totalCount;

      // Only initialize images if not already set (first time) or if images have changed
      const imagesChanged =
        state.randomizedImages.length === 0 ||
        state.randomizedImages.length !== action.payload.images.length;

      if (imagesChanged) {
        // First time initialization or images changed - randomize if needed
        if (action.payload.randomize) {
          state.randomizedImages = [...action.payload.images].sort(
            () => Math.random() - 0.5
          );
        } else {
          state.randomizedImages = [...action.payload.images];
        }
        state.currentIndex = 0;
        state.selectedOption = null;
        state.sortedItems = [];
      }
      // Otherwise, preserve existing randomizedImages, currentIndex, and sortedItems
    },
    selectOption: (state, action: PayloadAction<SelectOptionPayload>) => {
      state.selectedOption = action.payload.option;
    },
    navigateToIndex: (state, action: PayloadAction<NavigatePayload>) => {
      state.currentIndex = action.payload.index;
      const previousSelection = state.sortedItems.find(
        (item) => item.index === action.payload.index
      );
      state.selectedOption = previousSelection
        ? previousSelection.selectedOption
        : null;
    },
    submitAnswer: (state, action: PayloadAction<SubmitPayload>) => {
      const { index, option } = action.payload;

      const existingIndex = state.sortedItems.findIndex(
        (item) => item.index === index
      );

      if (existingIndex !== -1) {
        state.sortedItems[existingIndex] = {
          ...state.sortedItems[existingIndex],
          selectedOption: option,
        };
      } else {
        state.sortedItems.push({
          index,
          selectedOption: option,
        });
      }

      state.selectedOption = null;

      if (index < state.totalCount - 1) {
        state.currentIndex = index + 1;
      } else {
        // Mark as complete when last question is submitted
        state.isComplete = true;
      }
    },
    reset: (state) => {
      state.currentIndex = 0;
      state.selectedOption = null;
      state.sortedItems = [];
      state.totalCount = 0;
      state.randomizedImages = [];
      state.isComplete = false;
    },
  },
});

export const {
  initialize,
  selectOption,
  navigateToIndex,
  submitAnswer,
  reset,
} = microscopeSorterSlice.actions;

export const microscopeSorterReducer = microscopeSorterSlice.reducer;
export default microscopeSorterSlice.reducer;
